import type { AppointmentModel } from '@devexpress/dx-react-scheduler';
import dayjs from 'dayjs';
import { RRule, RRuleSet, rrulestr, Frequency, Weekday } from 'rrule';

import { MONTHS } from '@constants/date';
import {
  ROUTINES_RECURRENCE_APR,
  ROUTINES_RECURRENCE_AUG,
  ROUTINES_RECURRENCE_DEC,
  ROUTINES_RECURRENCE_EXDATE,
  ROUTINES_RECURRENCE_FEB,
  ROUTINES_RECURRENCE_JAN,
  ROUTINES_RECURRENCE_JUL,
  ROUTINES_RECURRENCE_JUN,
  ROUTINES_RECURRENCE_MAR,
  ROUTINES_RECURRENCE_MONTHDAY,
  ROUTINES_RECURRENCE_NOV,
  ROUTINES_RECURRENCE_NTHWEEKDAY,
  ROUTINES_RECURRENCE_OCT,
  ROUTINES_RECURRENCE_SEP,
} from '@constants/extension-codes';

import {
  combineDateTime,
  convertToISOStringNoPunc,
  createDateAsUTC,
} from '@utils/date';

import type {
  Repeat,
  TaskModel,
  InputModel,
  Timing,
  Extension,
  TaskModelInput,
} from 'generated/types';
import { DayOfWeek, TaskIntent, TaskStatus } from 'generated/types';

import { convertDateToUTC } from '../utils/date';
import { FhirResource } from './fhir-resource';

export type TimeSlot = {
  /**
   * Time that the slot begins
   */
  startTime: Date;
  /**
   * Time that the slot stops
   */
  endTime: Date;
};

export type RoutineEvent = AppointmentModel & {
  /**
   * Date that the routine recurrence started on
   */
  recurrenceStartDate?: Date;
  /**
   * Instructions or a description of the routine
   */
  instructions?: string;
};

export class RoutineRecord extends FhirResource {
  protected model: TaskModel;

  public constructor(model: TaskModel) {
    super(model);
    this.model = model;
  }

  public get name() {
    const { code } = this.model;
    return code?.text;
  }

  public get instructions() {
    return this.model.description;
  }

  public get dateRange() {
    const { input } = this.model;

    const timing = (input ?? []).find<
      InputModel & { valueTiming: Timing & { repeat: Repeat } }
    >((i): i is InputModel & { valueTiming: Timing & { repeat: Repeat } } =>
      Boolean(i.valueTiming?.repeat)
    )?.valueTiming;
    const recurrence = timing?.repeat;
    const recurrenceStartDate = recurrence?.boundsPeriod?.start
      ? dayjs(recurrence.boundsPeriod.start).toDate()
      : new Date();
    const recurrenceEndDate = recurrence?.boundsPeriod?.end
      ? dayjs(recurrence.boundsPeriod.end).toDate()
      : undefined;

    return {
      start: recurrenceStartDate,
      end: recurrenceEndDate,
    };
  }

  public get event(): RoutineEvent | null {
    const { input } = this.model;
    const { instructions, name } = this;

    const timing = (input ?? []).find<
      InputModel & { valueTiming: Timing & { repeat: Repeat } }
    >((i): i is InputModel & { valueTiming: Timing & { repeat: Repeat } } =>
      Boolean(i.valueTiming?.repeat)
    )?.valueTiming;
    const recurrence = timing?.repeat;

    if (!recurrence) {
      return null;
    }

    const recurrenceStartDate = recurrence.boundsPeriod?.start
      ? convertDateToUTC(dayjs(recurrence.boundsPeriod.start).toDate())
      : new Date();
    const bymonthday = this.getRuleMonthdays();
    const exDates = this.getRuleExdates();
    const bymonth = this.getRuleMonths();
    const nthweekday = this.getRuleNthWeekday();
    const rRuleSet = RoutineRecord.getRRuleSet(
      recurrence,
      bymonth,
      bymonthday,
      nthweekday
    );
    const startDate = dayjs(recurrenceStartDate).toDate();
    const endDate = dayjs(recurrenceStartDate)
      .add(recurrence.duration ?? 15, 'minutes')
      .toDate();
    const rRule = rRuleSet ? rRuleSet.valueOf().join('\n') : '';
    const routineEvent: RoutineEvent = {
      id: this.id ?? '',
      title: name ?? undefined,
      instructions: instructions ?? undefined,
      rRule,
      // Exclude dates on which the routine has been completed and any other dates that have been excluded
      // exDate: TODO,
      bymonth,
      startDate,
      recurrenceStartDate,
      endDate,
      exDate: exDates.reduce<string>((dateAcc, res, index) => {
        if (res) {
          const exDate = res;
          dateAcc = dateAcc.concat(`${index > 0 ? ',' : ''}${exDate}`);
        }
        return dateAcc;
      }, ''),
    };

    return routineEvent;
  }

  public static convertEventToModel(
    event: RoutineEvent,
    patient_id: string,
    isSingleOccurrence = false
  ): TaskModelInput {
    const {
      id,
      recurrenceStartDate,
      exDate,
      instructions,
      title,
      rRule,
      startDate,
      endDate,
    } = event;
    const rule = rRule ? RRule.fromString(rRule) : null;
    const {
      freq,
      until,
      count,
      interval,
      byweekday,
      bymonth,
      bymonthday,
      byhour,
      byminute,
      bynweekday,
    } = rule?.options ?? {};
    const duration = RoutineRecord.getRecurrenceDuration(startDate, endDate);
    const nthweekdayConfig = RoutineRecord.getRecurrenceNthWeekday(
      bynweekday?.[0]
    );
    const repeatStartDate = isSingleOccurrence
      ? dayjs(startDate).startOf('day')
      : (recurrenceStartDate ?? startDate);
    const repeatEndDate = isSingleOccurrence ? undefined : until;
    const timeOfDay = RoutineRecord.getRecurrenceTimeOfDay(
      byhour ?? [],
      byminute ?? []
    );
    const model: TaskModelInput = {
      id: typeof id === 'undefined' ? undefined : id.toString(),
      intent: TaskIntent.unknown,
      status: TaskStatus.accepted,
      extension: [
        ...(bymonthday ?? []).map<Extension>((month) => ({
          url: FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_MONTHDAY),
          valueInteger: month,
        })),
        ...(bymonth ?? [])
          .map<Extension | null>((month) =>
            RoutineRecord.getRecurrenceMonth(month)
          )
          .filter((e): e is Extension => Boolean(e)),
        ...RoutineRecord.getRecurrenceExdates(exDate),
        ...(nthweekdayConfig?.length ? [nthweekdayConfig[0]] : []),
      ],
      for: {
        reference: patient_id,
      },
      input: [
        {
          type: {
            coding: [
              {
                code: 'routine-definition',
                display: 'Routine definition',
              },
            ],
          },
          valueTiming: {
            repeat: {
              boundsPeriod: {
                start: repeatStartDate
                  ? createDateAsUTC(
                      dayjs(repeatStartDate).toDate()
                    ).toISOString()
                  : undefined,
                end: repeatEndDate
                  ? createDateAsUTC(dayjs(repeatEndDate).toDate()).toISOString()
                  : undefined,
              },
              dayOfWeek: RoutineRecord.getRecurrenceDayOfWeek(
                (nthweekdayConfig?.length
                  ? [nthweekdayConfig[1]]
                  : byweekday) ?? []
              ),
              periodUnit: RoutineRecord.getRecurrencePeriodUnit(freq),
              period: interval,
              count: isSingleOccurrence
                ? timeOfDay?.length
                : (count ?? undefined),
              timeOfDay,
              duration: duration,
              durationUnit: duration ? 'min' : undefined,
            },
          },
        },
      ],
      description: instructions,
      code: { text: title },
    };
    if (!model.extension?.length) {
      model.extension = undefined;
    }
    return model;
  }

  /**
   * Get the time slots for a routine event
   * @param routineEvent Routine's calendar event
   * @returns The times in a day that the routine event occurs
   */
  public static getTimeSlots(routineEvent: RoutineEvent): TimeSlot[] {
    const { rRule, startDate, endDate } = routineEvent;
    const duration = Math.abs(
      endDate ? dayjs(startDate).diff(endDate, 'minutes') : 15
    );
    if (!rRule) {
      return [];
    }
    const rule = RRule.fromString(
      `${rRule};DTSTART=${convertToISOStringNoPunc(new Date(startDate), {
        adjustTz: false,
      })}`
    );
    const { options } = rule;
    const { byhour, byminute } = options;
    const slots: TimeSlot[] = byhour.map((hour, index) => {
      const startTime = dayjs()
        .hour(hour)
        .minute(byminute[index])
        .second(0)
        .millisecond(0)
        .toDate();
      const endTime = dayjs()
        .hour(hour)
        .minute(byminute[index])
        .second(0)
        .millisecond(0)
        .add(duration, 'minutes')
        .toDate();
      return {
        endTime,
        startTime,
        id: undefined,
      };
    });
    return slots;
  }

  /**
   * Get the routines that fall between two dates
   * @param routines Routine events
   * @param rangeStart Min date
   * @param rangeEnd Max date
   * @returns Routine events that fall between `rangeStart` and `rangeEnd`
   */
  public static getRoutinesRange(
    routines: RoutineEvent[] = [],
    rangeStart: Date = new Date(),
    rangeEnd: Date = new Date()
  ): RoutineEvent[] {
    return routines.reduce<RoutineEvent[]>((acc, event) => {
      const { rRule, exDate, id, startDate, endDate } = event;
      const timeSlots = RoutineRecord.getTimeSlots(event);

      if (dayjs(rangeEnd).isBefore(startDate)) {
        return acc;
      }

      // Account for single occurrence routines
      if (!rRule) {
        if (
          dayjs(rangeStart).isBefore(startDate) &&
          (!endDate || dayjs(rangeEnd).isAfter(endDate))
        ) {
          // Only add the current routine event if it hasn't already been added
          const matchIndex = acc.findIndex(
            (curr) => curr.startDate.toString() === startDate.toString()
          );
          if (matchIndex === -1) {
            acc.push(event);
          }
        }
        return acc;
      }
      // Don't include dates that have been excluded from the routine recurrence
      const rule = rrulestr(
        [
          startDate
            ? `DTSTART:${convertToISOStringNoPunc(new Date(startDate))};`
            : '',
          rRule,
          exDate ? `EXDATE:${exDate}` : '',
        ]
          .filter((s) => Boolean(s))
          .join('\n')
      );
      const exDates =
        rule instanceof RRuleSet
          ? rule.exdates().map((date) => date.toISOString())
          : [];

      // Must subract 1 millisecond from range start to account for start-of-day
      const dates = rule
        .between(
          dayjs(rangeStart).subtract(1, 'millisecond').toDate(),
          rangeEnd
        )
        .map((date) => {
          return dayjs(date)
            .subtract(dayjs(date).utcOffset(), 'minutes')
            .toDate();
        });

      dates.forEach((date) => {
        if (exDates.includes(date.toISOString())) {
          return;
        }
        timeSlots.forEach(({ startTime, endTime }) => {
          const [hours, minutes] = [date.getHours(), date.getMinutes()];
          const [startHours, startMinutes] = [
            startTime.getHours(),
            startTime.getMinutes(),
          ];
          if (hours === startHours && minutes === startMinutes) {
            const instanceStartDate = date;
            const instanceEndDate = combineDateTime(date, endTime);
            if (
              dayjs(rangeStart).isBefore(instanceStartDate) ||
              dayjs(rangeEnd).isAfter(instanceEndDate)
            ) {
              // Only add the current routine event if it hasn't already been added
              const matchIndex = acc.findIndex((event) => {
                return (
                  event.id === id &&
                  new Date(event.startDate).toISOString() ===
                    new Date(instanceStartDate).toISOString() &&
                  (!event.endDate ||
                    new Date(event.endDate).toISOString() ===
                      new Date(instanceEndDate).toISOString())
                );
              });
              if (matchIndex === -1) {
                acc.push({
                  ...event,
                  startDate: instanceStartDate,
                  endDate: instanceEndDate,
                });
              }
            }
          }
        });
      });
      return acc;
    }, []);
  }

  public static getTodaysRoutines(routines: RoutineRecord[] = []) {
    return RoutineRecord.getRoutinesRange(
      routines
        .map(({ event }) => event)
        .filter((e): e is RoutineEvent => e !== null),
      dayjs().startOf('day').toDate(),
      dayjs().endOf('day').toDate()
    );
  }

  public static getRecurrenceDuration(
    startDate: Date | string | number,
    endDate: Date | string | number | null | undefined
  ): number {
    const duration =
      typeof endDate === 'undefined'
        ? 15
        : dayjs(endDate).diff(startDate, 'minutes');
    return duration;
  }

  private static getRRuleSet(
    recurrence: Repeat,
    bymonth: number[] | undefined,
    bymonthday: number[] | undefined,
    nthweekday: number | undefined
  ) {
    const rruleSet = new RRuleSet();
    const numTimeSlots = (recurrence.timeOfDay ?? []).length;
    const freq = RoutineRecord.getRuleFrequency(recurrence);
    const byweekday = RoutineRecord.getRuleDaysOfWeek(recurrence);

    /** This block is for dealing with multiple timeslots per day */
    const { byhour, byminute } = (recurrence.timeOfDay ?? [])
      .filter((t): t is string => Boolean(t))
      .sort((t_a, t_b) => {
        // Sort by time of day, asc
        const [h_a, m_a] = t_a.split(':').map((e) => Number(e));
        const [h_b, m_b] = t_b.split(':').map((e) => Number(e));
        if (h_a === h_b) {
          return m_a > m_b ? 1 : -1;
        }
        return h_a > h_b ? 1 : -1;
      })
      .reduce<{ byhour: number[]; byminute: number[] }>(
        (acc, curr) => {
          const [h, m] = curr.split(':').map((e) => Number(e));
          const date = new Date();
          date.setHours(h);
          date.setMinutes(m);
          date.setSeconds(0);
          date.setMilliseconds(0);
          const [newH, newM] = date
            .toISOString()
            .split('T')[1]
            .split(':')
            .map((e) => Number(e));
          acc.byhour.push(newH);
          acc.byminute.push(newM);
          return acc;
        },
        { byhour: [], byminute: [] }
      );
    // Populate an array so that only the correct times are included
    // E.g. byhour=[10,14], byminute=[0,30] would generate times [10:00,10:30,14:00,14:30] so we the following code to populate bysetpos. In this example we want [1,4] so that only 10:00 and 14:30 are included
    const bysetpos: number[] = [];
    for (const w of [...Array(byweekday.length || 1).keys()]) {
      const loops = [...Array(numTimeSlots).keys()];
      for (const j of loops) {
        const remaining = loops.length - (j + 1);
        bysetpos.push(
          numTimeSlots * (j + 1) - remaining + w * numTimeSlots ** 2
        );
      }
    }

    const until = recurrence.boundsPeriod?.end
      ? dayjs(recurrence.boundsPeriod.end).toDate()
      : undefined;
    const { count } = recurrence;
    const interval = recurrence.period ?? undefined;

    const firstByWeekdayIndex = byweekday.findIndex(Boolean);
    if (firstByWeekdayIndex > -1) {
      byweekday[firstByWeekdayIndex] = new Weekday(
        byweekday[firstByWeekdayIndex].weekday,
        nthweekday
      );
    }

    if (freq === undefined) {
      return undefined;
    }
    // Define recurrence rule
    rruleSet.rrule(
      new RRule({
        dtstart: dayjs(recurrence.boundsPeriod?.start).toDate(),
        freq,
        until,
        count,
        interval,
        byweekday,
        bymonth,
        bymonthday,
        bysetpos,
        byhour,
        byminute,
      })
    );

    return rruleSet;
  }

  private static getRuleFrequency(recurrence: Repeat) {
    // s | min | h | d | wk | mo | a
    const { periodUnit } = recurrence;
    if (!periodUnit) {
      return undefined;
    }
    switch (periodUnit) {
      case 'a':
        return RRule.YEARLY;
      case 'mo':
        return RRule.MONTHLY;
      case 'wk':
        return RRule.WEEKLY;
      case 'd':
      default:
        return RRule.DAILY;
    }
  }

  private static getRecurrencePeriodUnit(freq: Frequency | undefined) {
    if (freq === undefined) {
      return undefined;
    }
    switch (freq) {
      case Frequency.YEARLY:
        return 'a';
      case Frequency.MONTHLY:
        return 'mo';
      case Frequency.WEEKLY:
        return 'wk';
      case Frequency.DAILY:
      default:
        return 'd';
    }
  }

  private static getRuleDaysOfWeek(recurrence: Repeat) {
    const days = recurrence.dayOfWeek;
    const daysOfWeek = [];
    if (days?.includes(DayOfWeek.sun)) daysOfWeek.push(RRule.SU);
    if (days?.includes(DayOfWeek.mon)) daysOfWeek.push(RRule.MO);
    if (days?.includes(DayOfWeek.tue)) daysOfWeek.push(RRule.TU);
    if (days?.includes(DayOfWeek.wed)) daysOfWeek.push(RRule.WE);
    if (days?.includes(DayOfWeek.thu)) daysOfWeek.push(RRule.TH);
    if (days?.includes(DayOfWeek.fri)) daysOfWeek.push(RRule.FR);
    if (days?.includes(DayOfWeek.sat)) daysOfWeek.push(RRule.SA);
    return daysOfWeek;
  }

  private static getRecurrenceDayOfWeek(byweekday: number[]) {
    const daysOfWeek = [];
    if (byweekday.includes(RRule.SU.weekday)) daysOfWeek.push(DayOfWeek.sun);
    if (byweekday.includes(RRule.MO.weekday)) daysOfWeek.push(DayOfWeek.mon);
    if (byweekday.includes(RRule.TU.weekday)) daysOfWeek.push(DayOfWeek.tue);
    if (byweekday.includes(RRule.WE.weekday)) daysOfWeek.push(DayOfWeek.wed);
    if (byweekday.includes(RRule.TH.weekday)) daysOfWeek.push(DayOfWeek.thu);
    if (byweekday.includes(RRule.FR.weekday)) daysOfWeek.push(DayOfWeek.fri);
    if (byweekday.includes(RRule.SA.weekday)) daysOfWeek.push(DayOfWeek.sat);
    if (daysOfWeek.length) {
      return daysOfWeek;
    }
    return undefined;
  }

  private static getRecurrenceMonth(month: number): Extension | null {
    let url = null;
    switch (month) {
      case MONTHS.JANUARY:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_JAN);
        break;
      case MONTHS.FEBRUARY:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_FEB);
        break;
      case MONTHS.MARCH:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_MAR);
        break;
      case MONTHS.APRIL:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_APR);
        break;
      case MONTHS.MAY:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_MAR);
        break;
      case MONTHS.JUNE:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_JUN);
        break;
      case MONTHS.JULY:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_JUL);
        break;
      case MONTHS.AUGUST:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_AUG);
        break;
      case MONTHS.SEPTEMBER:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_SEP);
        break;
      case MONTHS.OCTOBER:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_OCT);
        break;
      case MONTHS.NOVEMBER:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_NOV);
        break;
      case MONTHS.DECEMBER:
        url = FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_DEC);
        break;
    }
    if (!url) {
      return null;
    }
    return {
      url,
      valueBoolean: true,
    };
  }

  private static getRecurrenceTimeOfDay(
    byhour: number[],
    byminute: number[]
  ): string[] | undefined {
    const timeOfDay = byhour.map((hour, index) => {
      let d = new Date();
      d.setMilliseconds(0);
      d.setSeconds(0);
      d.setHours(hour);
      d.setMinutes(byminute[index]);
      d = createDateAsUTC(d);
      return `${d.getHours().toString().padStart(2, '0')}:${d
        .getMinutes()
        .toString()
        .padStart(2, '0')}:00`;
    });
    if (!timeOfDay.length) {
      return undefined;
    }
    return timeOfDay;
  }

  /**
   * Get the recurrence Nth weekday config
   * @param bynweekday RRule bynweekday config
   * @returns Array containing the extension for the nthweekday, and the day of week
   */
  private static getRecurrenceNthWeekday(
    bynweekday?: number[] | null
  ): [Extension, number] | undefined {
    if (!bynweekday?.length) {
      return undefined;
    }
    const [day, week] = bynweekday;
    let nthweekday: string;
    switch (week) {
      case 1:
        nthweekday = 'First';
        break;
      case 2:
        nthweekday = 'Second';
        break;
      case 3:
        nthweekday = 'Third';
        break;
      case 4:
        nthweekday = 'Fourth';
        break;
      case -1:
      default:
        nthweekday = 'Last';
        break;
    }

    const extension: Extension = {
      url: FhirResource.padExtensionUrl(ROUTINES_RECURRENCE_NTHWEEKDAY),
      valueString: nthweekday,
    };

    return [extension, day];
  }

  public getModelInput() {
    return FhirResource.getCleanInput(this.model);
  }

  private getRuleMonths() {
    const recurrenceJanuary = this.findExtension(ROUTINES_RECURRENCE_JAN);
    const recurrenceFebruary = this.findExtension(ROUTINES_RECURRENCE_FEB);
    const recurrenceMarch = this.findExtension(ROUTINES_RECURRENCE_MAR);
    const recurrenceApril = this.findExtension(ROUTINES_RECURRENCE_APR);
    const recurrenceMay = this.findExtension(ROUTINES_RECURRENCE_MAR);
    const recurrenceJune = this.findExtension(ROUTINES_RECURRENCE_JUN);
    const recurrenceJuly = this.findExtension(ROUTINES_RECURRENCE_JUL);
    const recurrenceAugust = this.findExtension(ROUTINES_RECURRENCE_AUG);
    const recurrenceSeptember = this.findExtension(ROUTINES_RECURRENCE_SEP);
    const recurrenceOctober = this.findExtension(ROUTINES_RECURRENCE_OCT);
    const recurrenceNovember = this.findExtension(ROUTINES_RECURRENCE_NOV);
    const recurrenceDecember = this.findExtension(ROUTINES_RECURRENCE_DEC);

    const monthsOfTheYear = [];
    if (recurrenceJanuary?.valueBoolean) monthsOfTheYear.push(MONTHS.JANUARY);
    if (recurrenceFebruary?.valueBoolean) monthsOfTheYear.push(MONTHS.FEBRUARY);
    if (recurrenceMarch?.valueBoolean) monthsOfTheYear.push(MONTHS.MARCH);
    if (recurrenceApril?.valueBoolean) monthsOfTheYear.push(MONTHS.APRIL);
    if (recurrenceMay?.valueBoolean) monthsOfTheYear.push(MONTHS.MAY);
    if (recurrenceJune?.valueBoolean) monthsOfTheYear.push(MONTHS.JUNE);
    if (recurrenceJuly?.valueBoolean) monthsOfTheYear.push(MONTHS.JULY);
    if (recurrenceAugust?.valueBoolean) monthsOfTheYear.push(MONTHS.AUGUST);
    if (recurrenceSeptember?.valueBoolean)
      monthsOfTheYear.push(MONTHS.SEPTEMBER);
    if (recurrenceOctober?.valueBoolean) monthsOfTheYear.push(MONTHS.OCTOBER);
    if (recurrenceNovember?.valueBoolean) monthsOfTheYear.push(MONTHS.NOVEMBER);
    if (recurrenceDecember?.valueBoolean) monthsOfTheYear.push(MONTHS.DECEMBER);
    return monthsOfTheYear;
  }

  private getRuleMonthdays() {
    const extensions = this.findExtensions(ROUTINES_RECURRENCE_MONTHDAY);
    return (extensions ?? [])
      .map(({ valueInteger }) => valueInteger)
      .filter(
        (valueInteger): valueInteger is number =>
          valueInteger !== null &&
          valueInteger !== undefined &&
          !isNaN(valueInteger)
      );
  }

  private getRuleExdates() {
    const extensions = this.findExtensions(ROUTINES_RECURRENCE_EXDATE);
    return (extensions ?? [])
      .map(({ valueDateTime }) =>
        valueDateTime ? convertToISOStringNoPunc(new Date(valueDateTime)) : null
      )
      .filter(
        (valueDateTime): valueDateTime is string => valueDateTime !== null
      );
  }
  private static getRecurrenceExdates(exDate: string | undefined): Extension[] {
    if (!exDate) {
      return [];
    }
    const exDates = exDate.split(',');
    return exDates.map<Extension>((datetime) => {
      const [date, time] = datetime.split('T');
      const [year, month, day] = [
        date.slice(0, 4).padStart(4, '0'),
        date.slice(4, 6).padStart(2, '0'),
        date.slice(6, 8).padStart(2, '0'),
      ];
      const [hour, minute, second] = [
        time.slice(0, 2).padStart(2, '0'),
        time.slice(2, 4).padStart(2, '0'),
        time.slice(4, 6).padStart(2, '0'),
      ];
      return {
        url: this.padExtensionUrl(ROUTINES_RECURRENCE_EXDATE),
        valueDateTime: `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`,
      };
    });
  }

  private getRuleNthWeekday() {
    const extension = this.findExtension(ROUTINES_RECURRENCE_NTHWEEKDAY);
    const value = extension?.valueString;
    if (value === '') {
      return undefined;
    }
    switch (value) {
      case 'First':
        return 1;
      case 'Second':
        return 2;
      case 'Third':
        return 3;
      case 'Fourth':
        return 4;
      case 'Last':
      default:
        return -1;
    }
  }
}
